/**
 * Input texte unifié — design system champ-erp.
 *
 * Extrait des duplications repérées dans `DomaineSettings.tsx` et
 * `ProfileMfaSetup.tsx` (mêmes classes, deux palettes d'erreur divergentes).
 * On unifie sur la palette sémantique `rust-*` (cohérente avec le reste de
 * l'app : Toaster, alerts, boutons danger).
 *
 * Variantes de taille :
 *   - `default` (h-11) : pages settings desktop / formulaires classiques.
 *   - `field` (h-12)   : cible terrain (mains mouillées, mobile, soleil).
 *
 * Forwarde la `ref` pour rester compatible react-hook-form
 * (`<Input {...register("nom")} />`). Préserve `className` si surcharge ponctuelle.
 */

import { forwardRef, type InputHTMLAttributes } from "react";

type InputSize = "default" | "field";

// On retire `size` de l'interface HTML (number) pour la rebrancher en variante
// visuelle ("default" | "field"). La prop HTML `size` est rarement utilisée
// sur les inputs texte modernes ; si besoin un jour, on l'exposera via une
// autre prop (ex. `htmlSize`).
export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  /** True si le champ est en erreur — applique une bordure rust + ring rouge. */
  error?: boolean;
  /** Taille du champ. `field` = h-12, recommandé pour cibles terrain. */
  size?: InputSize;
}

const BASE_CLASSES =
  "block w-full rounded-xl border bg-white text-champagne-900 placeholder:text-champagne-800/30 focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-champagne-50 disabled:text-champagne-700";

const SIZE_CLASSES: Record<InputSize, string> = {
  default: "h-11 px-3.5 text-[14px]",
  field: "h-12 px-4 text-base",
};

const STATE_CLASSES = {
  ok: "border-champagne-800/20 focus:border-champagne-500 focus:ring-champagne-500/20",
  error: "border-rust-600 focus:border-rust-700 focus:ring-rust-600/20",
};

/**
 * Helper pour appliquer le même style à un `<select>`, `<textarea>` ou tout
 * élément qui n'est pas un `<input>` mais qui doit suivre la même charte
 * (mêmes hauteurs, mêmes états error/focus). Utilisé tant que le design system
 * n'a pas de composants `<Select>` / `<Textarea>` dédiés.
 */
export function inputClasses(error = false, size: InputSize = "default"): string {
  return [BASE_CLASSES, SIZE_CLASSES[size], error ? STATE_CLASSES.error : STATE_CLASSES.ok].join(
    " ",
  );
}

// Largeurs des filtres de liste. Classes écrites en toutes lettres : Tailwind
// scanne le source, une classe construite dynamiquement n'est jamais générée.
const FILTER_MAX_WIDTHS = {
  180: "sm:max-w-[180px]",
  200: "sm:max-w-[200px]",
  220: "sm:max-w-[220px]",
} as const;

export type FilterMaxWidth = keyof typeof FILTER_MAX_WIDTHS;

/**
 * Classes d'un `<select>` de filtre de liste.
 *
 * La largeur n'est bornée qu'à partir de `sm`. Sous 640 px les filtres se
 * décalent chacun sur sa ligne : des largeurs différentes y sont bancales, et
 * un filtre étroit à côté d'une recherche pleine largeur donne un bloc de
 * guingois. En pleine largeur ils s'alignent, et la cible tactile grandit.
 */
export function filterSelectClasses(
  maxWidth: FilterMaxWidth = 200,
  size: InputSize = "default",
): string {
  return `${inputClasses(false, size)} ${FILTER_MAX_WIDTHS[maxWidth]}`;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  // `size` est une prop HTML standard sur <input> mais peu utilisée ; on la
  // capture pour notre variante visuelle et on ne la propage pas au DOM.
  { error = false, size = "default", className, ...rest },
  ref,
) {
  const composed = [
    BASE_CLASSES,
    SIZE_CLASSES[size],
    error ? STATE_CLASSES.error : STATE_CLASSES.ok,
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  return <input ref={ref} className={composed} {...rest} />;
});
